import { Fragment, useEffect, useState } from "react"
import { observer } from "mobx-react"
import classNames from "classnames"
import { FooterBorder, useStore } from "@features/common"
import {
  IconSearch,
  IconChevronRight,
  IconChevronLeft,
  IconClose,
} from "@lib/icons"

import type { ChangeEvent } from "react"

type SearchFormProps = {
  initialValue?: string
  onBlur: (query: string) => Promise<void>
  onClose: () => void
  placeholder?: string
}

export const SearchForm = observer(function SearchForm(props: SearchFormProps) {
  const { initialValue = "", onBlur, onClose, placeholder = "Поиск" } = props
  const {
    AnalyticsModel: {
      faqSearchResultPaginationNextClickHandler,
      faqSearchResultPaginationPrevClickHandler,
    },
    FaqModel: { search, searchResultCount, getTextByKey },
  } = useStore()
  const [query, setQuery] = useState(initialValue)
  const [matchNodes, setMatchNodes] = useState<HTMLElement[]>([])
  const [currentNode, setCurrentNode] = useState<HTMLElement | null>(null)
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0)
  const [isSearching, setSearching] = useState(false)

  useEffect(() => {
    if (query.length) {
      const nodes = document.querySelectorAll("mark")
      const nodesArray = Array.from(nodes)
      setMatchNodes(nodesArray)
      setCurrentNode(nodesArray[0])
      setCurrentNodeIndex(0)
      setSearching(true)
    } else {
      setMatchNodes([])
      setCurrentNode(null)
      setSearching(false)
      setCurrentNodeIndex(0)
    }
  }, [query])

  useEffect(() => {
    if (matchNodes.length) {
      setCurrentNode(matchNodes[currentNodeIndex])
    }
  }, [matchNodes, currentNodeIndex])

  useEffect(() => {
    if (currentNode) {
      currentNode.classList.add("bg-opacity-100")
      currentNode.scrollIntoView({ block: "center" })
    }
  }, [currentNode])

  function handleQueryChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target
    setQuery(value)
    search(value)
  }

  function handleBlur() {
    onBlur(query)
  }

  function removeHighlightFromCurrentNode() {
    currentNode?.classList.remove("bg-opacity-100")
  }

  function handlePrevClick() {
    faqSearchResultPaginationPrevClickHandler()
    removeHighlightFromCurrentNode()
    if (currentNodeIndex === 0) {
      setCurrentNodeIndex(matchNodes.length - 1)
    } else {
      setCurrentNodeIndex((state) => state - 1)
    }
  }

  function handleNextClick() {
    faqSearchResultPaginationNextClickHandler()
    removeHighlightFromCurrentNode()
    if (currentNodeIndex === matchNodes.length - 1) {
      setCurrentNodeIndex(0)
    } else {
      setCurrentNodeIndex((state) => state + 1)
    }
  }

  const disabledNextPrevButton = searchResultCount <= 1

  return (
    <header className="relative mb-2 w-full bg-gray-400 ">
      <section className="container">
        <div className="space-x-3 px-0.5 py-2 flex items-center justify-between laptop:space-x-4 tablet:px-4 tablet:py-4">
          <div className="relative flex-grow">
            <IconSearch className="text-white absolute top-1/2 left-1 transform -translate-y-1/2 w-5.5 h-5.5 tablet:left-2" />
            <input
              autoFocus
              type="text"
              placeholder={placeholder}
              onChange={handleQueryChange}
              onBlur={handleBlur}
              value={query}
              className={classNames(
                "pb-2.5 pl-9 w-full h-10 font-sans text-lg font-normal text-white caret-white",
                "bg-transparent border-t-0 border-r-0 border-l-0 border-b border-b-gray-300 placeholder-gray-250",
                "transition-colors duration-100 ease-in",
                "tablet:pb-2 tablet:pl-10 tablet:h-12",
                "focus:border-b-white focus:outline-none focus:ring-0",
                searchResultCount ? "pr-20" : "pr-4",
              )}
              data-test-id="faq__search_input"
            />
            {isSearching && (
              <div className="absolute top-1/2 right-1 transform -translate-y-1/2 space-x-1 flex text-base text-gray-250 text-right tablet:right-2">
                {searchResultCount ? (
                  <Fragment>
                    <span>{currentNodeIndex + 1}</span>
                    <span>{getTextByKey("search.result_counter.divider")}</span>
                    <span>{searchResultCount}</span>
                  </Fragment>
                ) : (
                  <Fragment>
                    <span>{searchResultCount}</span>
                    <span>{getTextByKey("search.result_counter.divider")}</span>
                    <span>{searchResultCount}</span>
                  </Fragment>
                )}
              </div>
            )}
          </div>
          <div className="space-x-1 inline-flex flex-shrink-0">
            <button
              disabled={disabledNextPrevButton}
              onClick={handlePrevClick}
              className="inline-flex flex-shrink-0 text-gray-250 transition-colors duration-150 ease-in hover:text-white  focus:text-gray-200 focus:outline-none disabled:text-gray-300 disabled:cursor-default"
              data-test-id="faq__search_prev"
            >
              <IconChevronLeft />
            </button>
            <button
              disabled={disabledNextPrevButton}
              onClick={handleNextClick}
              className="inline-flex flex-shrink-0 text-gray-250 transition-colors duration-150 ease-in hover:text-white focus:text-gray-200 focus:outline-none disabled:text-gray-300 disabled:cursor-default"
              data-test-id="faq__search_next"
            >
              <IconChevronRight />
            </button>
          </div>
          <button
            className="inline-flex flex-shrink-0 text-white transition-colors duration-150 ease-in hover:text-gray-200  focus:text-gray-200 focus:outline-none"
            onClick={onClose}
            data-test-id="faq__search_close"
          >
            <IconClose />
          </button>
        </div>
      </section>
      <FooterBorder className="h-2 w-full text-gray-400 absolute top-full" />
    </header>
  )
})
